import React from 'react';
import { formatLicenseCount } from '../utils/billingHelpers';

const SubscriptionSelector = ({ subscriptions, onSelect, loading }) => {
// Helper function to get license count for a subscription
const getLicenseDisplay = (subscription) => {
    const billingModel = subscription.BillingModel || subscription.billingModel || 'PerUser';
    
    if (billingModel === 'PerUser') {
      // PerUser: Use Quantity from Subscriptions table
      const quantity = subscription.Quantity || subscription.quantity;
      if (quantity) {
        return {
          count: quantity,
          label: quantity === 1 ? 'License' : 'Licenses'
        };
      }
    } else if (billingModel === 'FlatRate' || billingModel === 'Hybrid') {
      // FlatRate: Get user count from DimensionLimits
      const dimensionLimits = subscription.DimensionLimits || subscription.dimensionLimits || [];
      
      const userCountDimension = dimensionLimits.find(d => 
        (d.DimensionIdentifier || d.dimensionIdentifier) === 'user_count'
      );
      
      if (userCountDimension) {
        const isUnlimited = userCountDimension.IsUnlimited || userCountDimension.isUnlimited;
        const includedQty = userCountDimension.IncludedQuantity || userCountDimension.includedQuantity || 0;
        const purchasedQty = userCountDimension.PurchasedQuantity || userCountDimension.purchasedQuantity || 0;
        const totalQty = includedQty + purchasedQty;
        
        return {
          count: isUnlimited ? 'Unlimited' : totalQty,
          label: (isUnlimited || totalQty !== 1) ? 'Licenses' : 'License',
          isUnlimited: isUnlimited
        };
      } else {
        // Fallback: If dimensionLimits not available, show placeholder
        return {
          count: 'Info',
          label: 'Unavailable',
          isMissing: true
        };
      }
    }
    
    return null;
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      animation: 'fadeIn 0.2s ease-in'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '2rem',
        maxWidth: '600px',
        width: '90%',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)'
      }}>
        {/* Header */}
        <div style={{ marginBottom: '1.5rem', textAlign: 'center' }}>
          <h2 style={{ margin: 0, color: '#333', fontSize: '1.75rem', marginBottom: '0.5rem' }}>
            Select Subscription
          </h2>
          <p style={{ margin: 0, color: '#666', fontSize: '0.95rem' }}>
            You have access to multiple subscriptions. Please choose one to manage.
          </p>
        </div>

        {/* Loading State */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Loading subscriptions...</span>
            </div>
            <p style={{ marginTop: '1rem', color: '#666' }}>Loading your subscriptions...</p>
          </div>
        )}

        {/* Subscriptions List */}
        {!loading && subscriptions && subscriptions.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {subscriptions.map((subscription) => {
              const subscriptionName = subscription.SubscriptionName || subscription.subscriptionName || 'Unnamed Subscription';
              const planName = subscription.PlanName || subscription.planName;  // Get the friendly plan name
              const subId = subscription.SubscriptionID || subscription.subscriptionID;
              const marketplaceId = subscription.MarketplaceSubscriptionId || subscription.marketplaceSubscriptionId;
              const licenseInfo = getLicenseDisplay(subscription);
              const isSuspended = subscription.SaaSSubscriptionStatus === 'Suspended' || 
                                  subscription.saaSSubscriptionStatus === 'Suspended';
              
              return (
                <button
                  key={subId}
                  onClick={() => onSelect(subId)}
                  style={{
                    padding: '1.5rem',
                    border: isSuspended ? '2px solid #ffc107' : '2px solid #e0e0e0',
                    borderRadius: '8px',
                    background: isSuspended ? '#fff3cd' : 'white',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.75rem',
                    position: 'relative'
                  }}
                  onMouseEnter={(e) => {
                    if (isSuspended) {
                      e.currentTarget.style.borderColor = '#fd7e14';
                      e.currentTarget.style.background = '#ffe8a1';
                    } else {
                      e.currentTarget.style.borderColor = '#0d6efd';
                      e.currentTarget.style.background = '#f8f9ff';
                    }
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = isSuspended 
                      ? '0 4px 12px rgba(255, 193, 7, 0.3)' 
                      : '0 4px 12px rgba(13, 110, 253, 0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = isSuspended ? '#ffc107' : '#e0e0e0';
                    e.currentTarget.style.background = isSuspended ? '#fff3cd' : 'white';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  {/* Suspended Badge */}
                  {isSuspended && (
                    <div style={{
                      position: 'absolute',
                      top: '0.75rem',
                      right: '0.75rem',
                      padding: '0.4rem 0.9rem',
                      background: '#dc3545',
                      color: 'white',
                      borderRadius: '12px',
                      fontSize: '0.75rem',
                      fontWeight: '700',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Suspended
                    </div>
                  )}
                  
                  {/* Subscription Name and Plan Name */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', paddingRight: isSuspended ? '7rem' : '0' }}>
                    <h3 style={{ 
                      margin: 0, 
                      fontSize: '1.35rem', 
                      color: isSuspended ? '#856404' : '#333',
                      fontWeight: '600'
                    }}>
                      {subscriptionName}
                    </h3>
                    {planName && (
                      <p style={{
                        margin: 0,
                        fontSize: '0.95rem',
                        color: isSuspended ? '#856404' : '#666',
                        fontWeight: '500'
                      }}>
                        Plan: {planName}
                      </p>
                    )}
                  </div>
                  
                  {/* Warning Message for Suspended */}
                  {isSuspended && (
                    <div style={{
                      padding: '0.5rem',
                      background: '#fff',
                      border: '1px solid #ffc107',
                      borderRadius: '6px',
                      fontSize: '0.85rem',
                      color: '#856404'
                    }}>
                      This subscription is suspended. Management functions are disabled and users cannot access reports.
                    </div>
                  )}
                  
                  {/* Marketplace Subscription ID and Licenses */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                    {marketplaceId && (
                      <span style={{
                        padding: '0.4rem 0.9rem',
                        background: isSuspended ? '#fff' : '#e3f2fd',
                        color: isSuspended ? '#856404' : '#1976d2',
                        borderRadius: '12px',
                        fontSize: '0.85rem',
                        fontWeight: '600',
                        fontFamily: 'monospace',
                        border: isSuspended ? '1px solid #ffc107' : 'none'
                      }}>
                        {marketplaceId}
                      </span>
                    )}
                    
                    {licenseInfo && (
                      <span style={{
                        padding: '0.4rem 0.9rem',
                        background: licenseInfo.isUnlimited ? '#fff3cd' : (isSuspended ? '#fff' : '#e8f5e9'),
                        color: licenseInfo.isUnlimited ? '#856404' : (isSuspended ? '#856404' : '#2e7d32'),
                        borderRadius: '12px',
                        fontSize: '0.85rem',
                        fontWeight: '600',
                        border: isSuspended ? '1px solid #ffc107' : 'none'
                      }}>
                        {licenseInfo.count} {licenseInfo.label}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* No Subscriptions */}
        {!loading && (!subscriptions || subscriptions.length === 0) && (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p style={{ color: '#666', fontSize: '1rem' }}>
              No subscriptions found. Please contact support.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionSelector;
